import { State, Actions, MutationTypes, Mutations, Getters } from './StufoeEvents.types';
import { StufoeEventCategory } from '../../../scripts/api/models/events';

interface StufoeEventWithDates {
    uid: number
    categoryUid: number
    title: string
    type: string
    location: string
    address: string
    zipCode: string
    city: string
    state: string
    registrationAllowed: boolean

    date: Date
    endDate: Date

    startTime: string | null
    endTime: string | null

    // a timestamp when the event overlay was last published (= hidden set to 0)
    lastPublished: number
}

class StufoeEventCategoryWithChildren implements StufoeEventCategory {
    public constructor (
        public readonly uid: number,
        public readonly parent: number,
        public readonly title: string,
        public sorting: number,
        public children: StufoeEventCategoryWithChildren[],
        public events: StufoeEventWithDates[]
    ) {}

    public get eventsToShow (): StufoeEventWithDates[] {
        const now = new Date();
        if (state.showPastEvents) {
            return this.events.filter((event) => now > event.date);
        }
        return this.events.filter((event) => now < event.date);
    }
}

const state: State = {
    topLevelCategories: null,

    showPastEvents: false,
};

const mutations: Mutations = {
    SET_VIEW (state: State, { showPastEvents }): void {
        state.showPastEvents = showPastEvents;
    },

    LOAD_EVENTS (state: State, { events, categories }): void {
        const eventsWithDates: StufoeEventWithDates[] = events
            .map((event) => {
                const startDate = new Date(event.date ?? '');
                const endDate = new Date(event.endDate ?? '');

                return {
                    ...event,
                    date: startDate,
                    endDate: endDate,
                };
            });

        type EventsByCategory = {[categoryId: number]: StufoeEventWithDates[]}
        const eventsByCategory: EventsByCategory = eventsWithDates
            .reduce<EventsByCategory>(
                (acc: EventsByCategory, event: StufoeEventWithDates) => {
                    const categoryUid = event.categoryUid;
                    acc[categoryUid] = acc[categoryUid] || [];
                    acc[categoryUid].push(event);
                    return acc;
                },
                {}
            );

        // create a list of categories indexed by parent ID
        type CategoriesByParent = {[key: number]: StufoeEventCategory[]};
        const categoriesByParent: CategoriesByParent = categories
            .filter((category) => category.parent > 0)
            .reduce<CategoriesByParent>(
                (acc: CategoriesByParent, category) => {
                    const parentId = category.parent;
                    acc[parentId] = acc[parentId] || [];
                    acc[parentId].push(category);
                    return acc;
                },
                {}
            );

        // Add child categories and events to all categories
        const mapCategory = (category: StufoeEventCategory): StufoeEventCategoryWithChildren => {
            const mappedChildren = categoriesByParent[category.uid] ?? [];
            const sortedEvents = (eventsByCategory[category.uid] ?? [])
                .sort((left, right) => {
                    if (left.date && right.date) {
                        return right.date < left.date ? 1 : -1;
                    }
                    return -1;
                });

            return new StufoeEventCategoryWithChildren(
                category.uid,
                category.parent,
                category.title,
                category.sorting,
                mappedChildren.map(mapCategory),
                sortedEvents
            );
        };
        const mappedCategories = categories.map(mapCategory);

        const rootCategory = categories.find((category) => category.parent === 0);

        if (rootCategory === undefined) {
            throw new Error('There must be one category without a parent category');
        }

        state.topLevelCategories = mappedCategories.filter((category) => category.parent === rootCategory.uid);
    },
};

const actions: Actions = {
    setView ({ commit }, { showPastEvents }): void {
        commit(MutationTypes.SET_VIEW, { showPastEvents });
    },

    loadEvents ({ commit }, { events, categories }): void {
        commit(MutationTypes.LOAD_EVENTS, { events, categories });
    },
};

const getters: Getters = {
    topLevelCategories (state: State): StufoeEventCategory[] {
        return state.topLevelCategories ?? [];
    },

    showPastEvents (state: State): boolean {
        return state.showPastEvents;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
